import { defineComponent as _defineComponent } from 'vue';
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue";
const _hoisted_1 = { class: "font-weight-bold mb-1 d-flex align-center" };
const _hoisted_2 = { class: "text-caption d-flex align-center" };
const _hoisted_3 = {
    key: 0,
    class: "mt-4"
};
import { computed, inject, nextTick, onMounted, reactive, ref, watch } from 'vue';
import KrumosCalendar from '@/components/KrumosCalendar.vue';
import { getAssignedResources, getResourceGroups } from '@/model/Product';
import { ProductServiceSymbol } from "@/InjectionSymbols";
import { Formatter } from "@/util/Formatter";
import BookingService from '@/service/BookingService';
import { DateUtil } from "@/util/DateUtil";
import { ResourceType } from "@/model/enum/ResourceType";
export default /*@__PURE__*/ _defineComponent({
    __name: 'AvailabilityListView',
    setup(__props) {
        const productService = inject(ProductServiceSymbol);
        const dataLoaded = ref(false);
        // Date selection
        const selectedDateFrom = ref(new Date());
        const selectedDateTo = ref(new Date());
        // Product selection
        const products = ref([]);
        const selectedProduct = ref(null);
        // Resource group selection
        const selectedResourceGroup = ref(null);
        // Resource selection
        const selectedResources = ref([]);
        const selectedTimeslotPerProduct = reactive({}); // productId -> slotId|null
        const bookingService = new BookingService();
        // Cache for availabilities: productId -> slotId (or 'day') -> Record<resourceId, available>
        const availabilities = reactive({});
        const loadingAvailabilities = reactive({});
        async function fetchAvailability(product, slot) {
            const productId = product.id;
            const slotKey = slot ? String(slot.id) : 'day';
            if (!availabilities[productId])
                availabilities[productId] = {};
            if (!loadingAvailabilities[productId])
                loadingAvailabilities[productId] = {};
            if (availabilities[productId][slotKey])
                return; // already fetched
            loadingAvailabilities[productId][slotKey] = true;
            let from, to;
            if (slot) {
                from = new Date(selectedDateFrom.value);
                from.setHours(slot.resourceOccupationStart.hour(), slot.resourceOccupationStart.minute(), 0, 0);
                to = new Date(selectedDateTo.value);
                to.setHours(slot.resourceOccupationEnd.hour(), slot.resourceOccupationEnd.minute(), 0, 0);
            }
            else {
                from = new Date(selectedDateFrom.value);
                to = new Date(selectedDateTo.value);
                if (product.priceStructure.pricedPerNight) {
                    from.setHours(12, 0, 0, 0);
                    to.setHours(11, 59, 59, 0);
                    // When availabilities are checked based on a resource, that resource might be used in different kind of
                    // products. For overnight products, we must adjust the to date to the next day if the from and to dates are the same.
                    if (from.getDate() === to.getDate()) {
                        to.setDate(to.getDate() + 1);
                    }
                }
                else {
                    from.setHours(0, 0, 0, 0);
                    to.setHours(23, 59, 59, 0);
                }
            }
            try {
                availabilities[productId][slotKey] = await bookingService.loadAvailabilitiesForProductSimple(productId, from, to);
            }
            catch (e) {
                availabilities[productId][slotKey] = {};
            }
            finally {
                loadingAvailabilities[productId][slotKey] = false;
                await nextTick();
            }
        }
        function getAvailability(product, slot, resourceId) {
            const productId = product.id;
            const slotKey = slot ? String(slot.id) : 'day';
            return availabilities[productId]?.[slotKey]?.[resourceId] ?? 0;
        }
        function isLoadingAvailability(product, slot) {
            const productId = product.id;
            const slotKey = slot ? String(slot.id) : 'day';
            return loadingAvailabilities[productId]?.[slotKey] ?? false;
        }
        onMounted(async () => {
            // Load all products
            products.value = await productService.loadAllProducts();
            dataLoaded.value = true;
        });
        // Filtered products based on other selections
        const filteredProducts = computed(() => {
            if (selectedProduct.value) {
                // If a product is selected, return only that product
                return [selectedProduct.value];
            }
            return products.value.filter(product => {
                if (!selectedResourceGroup.value && selectedResources.value.length === 0) {
                    return true;
                }
                // Filter by resource group
                if (selectedResourceGroup.value && product.resourceGroups?.some(g => g.id?.toString() === selectedResourceGroup.value?.id?.toString())) {
                    return true;
                }
                // Filter by resource(s)
                if (selectedResourceGroup.value || selectedResources.value.length > 0) {
                    const allProductResources = [
                        ...(product.resources || []),
                        ...((product.resourceGroups || []).flatMap(g => g.resources || []))
                    ];
                    if (selectedResourceGroup.value) {
                        for (const res of selectedResourceGroup.value.resources) {
                            if (allProductResources.some(r => r.id.toString() === res.id.toString()))
                                return true;
                        }
                    }
                    if (selectedResources.value) {
                        for (const res of selectedResources.value) {
                            if (allProductResources.some(r => r.id.toString() === res.id.toString()))
                                return true;
                        }
                    }
                }
                return false;
            });
        });
        // All resource groups across all products
        const allResourceGroups = computed(() => {
            const seen = new Set();
            const groups = [];
            for (const product of products.value) {
                for (const group of getResourceGroups(product, ResourceType.RESOURCE) || []) {
                    if (!group.id)
                        continue;
                    if (!seen.has(group.id.toString())) {
                        seen.add(group.id.toString());
                        groups.push(group);
                    }
                }
            }
            return groups;
        });
        // All resources across all products
        const allResources = computed(() => {
            const seen = new Set();
            const resources = [];
            for (const product of products.value) {
                for (const group of product.resourceGroups || []) {
                    for (const resource of group.resources || []) {
                        if (!resource.id)
                            continue;
                        if (!seen.has(resource.id.toString())) {
                            seen.add(resource.id.toString());
                            resources.push(resource);
                        }
                    }
                }
                for (const resource of product.resources || []) {
                    if (!resource.id)
                        continue;
                    if (!seen.has(resource.id.toString())) {
                        seen.add(resource.id.toString());
                        resources.push(resource);
                    }
                }
            }
            return resources;
        });
        // Filtered resource groups based on other selections
        const filteredResourceGroups = computed(() => {
            return allResourceGroups.value.filter(group => {
                // Filter by product
                if (selectedProduct.value) {
                    const productHasResourceGroup = selectedProduct.value.resourceGroups?.some(g => g.id?.toString() === group.id?.toString());
                    const productHasGroupResource = selectedProduct.value.resourceGroups?.some(rg => rg.resources.some(r => group.resources.find(resourceInGroup => resourceInGroup.id?.toString() === r.id?.toString())));
                    if (!productHasResourceGroup && !productHasGroupResource)
                        return false;
                }
                // Filter by resource(s)
                if (selectedResources.value.length > 0) {
                    const hasResource = group.resources?.some(r => selectedResources.value.some(sel => sel.id?.toString() === r.id?.toString()));
                    if (!hasResource)
                        return false;
                }
                return true;
            });
        });
        // Filtered resources based on other selections
        const filteredResources = computed(() => {
            return allResources.value.filter(resource => {
                // Filter by product
                if (selectedProduct.value) {
                    const allProductResources = [
                        ...(selectedProduct.value.resources || []),
                        ...((selectedProduct.value.resourceGroups || []).flatMap(g => g.resources || []))
                    ];
                    if (!allProductResources.some(r => r.id?.toString() === resource.id?.toString()))
                        return false;
                }
                // Filter by resource group
                if (selectedResourceGroup.value) {
                    if (!selectedResourceGroup.value.resources?.some(r => r.id?.toString() === resource.id?.toString()))
                        return false;
                }
                return true;
            });
        });
        function onDateSelected(dateFrom, dateTo) {
            for (let availabilitiesKey in availabilities) {
                availabilities[availabilitiesKey] = {};
            }
            selectedDateFrom.value = dateFrom;
            selectedDateTo.value = dateTo;
        }
        function selectTimeslot(productId, slotId) {
            selectedTimeslotPerProduct[productId] = selectedTimeslotPerProduct[productId] === slotId ? null : slotId;
        }
        function shouldShowResourceTable(product) {
            // If product has timeslots, show table only if a slot is selected
            if (product.timeslots && product.timeslots.length) {
                return selectedTimeslotPerProduct[product.id] != null;
            }
            // If no timeslots, always show
            return true;
        }
        function getSelectedSlot(product) {
            if (product.timeslots && product.timeslots.length) {
                const slotId = selectedTimeslotPerProduct[product.id];
                return product.timeslots.find(s => s.id === slotId) || null;
            }
            return null;
        }
        function timeslotCardColor(product, slot) {
            const ratio = timeslotAvailabilityRatio(product, slot);
            if (ratio === null)
                return 'bg-grey-lighten-4';
            if (ratio === 0)
                return 'bg-red-lighten-4';
            if (ratio >= 0.3)
                return 'bg-green-lighten-4';
            return 'bg-orange-lighten-4';
        }
        function timeslotAvailabilityRatio(product, slot) {
            const total = getAssignedResources(product, ResourceType.RESOURCE).reduce((sum, r) => sum + (Number(r.totalQuantity) ?? 0), 0);
            if (total === 0)
                return null;
            const available = getAssignedResources(product, ResourceType.RESOURCE).reduce((sum, r) => sum + getAvailability(product, slot, Number(r.id)), 0);
            return available / total;
        }
        function resourceAvailabilityRatio(product, resource) {
            const total = Number(resource.totalQuantity) ?? 0;
            if (total === 0)
                return null;
            const available = getAvailability(product, getSelectedSlot(product), Number(resource.id));
            return available / total;
        }
        function availabilityIcon(ratio) {
            if (ratio === null)
                return 'mdi-help-circle';
            if (ratio === 0)
                return 'mdi-close-circle';
            if (ratio >= 0.3)
                return 'mdi-check-circle';
            return 'mdi-alert-circle';
        }
        function availabilityIconColor(ratio) {
            if (ratio === null)
                return 'grey';
            if (ratio === 0)
                return 'red';
            if (ratio >= 0.3)
                return 'green';
            return 'orange';
        }
        function getFilteredTimeslots(product) {
            if (!product.timeslots || product.timeslots.length === 0) {
                return [];
            }
            // Filter timeslots to only include those that match the selected day of week
            return product.timeslots.filter(timeslot => timeslot.weekdays.some(day => day == DateUtil.getDayOfWeek(selectedDateFrom.value)));
        }
        const hasActiveFilter = computed(() => {
            return !!(selectedProduct.value ||
                selectedResourceGroup.value ||
                (selectedResources.value && selectedResources.value.length > 0));
        });
        // Watch for timeslot selection and fetch data
        watch(() => Object.entries(selectedTimeslotPerProduct).map(([pid, sid]) => [Number(pid), sid]), (entries) => {
            for (const [productId, slotId] of entries) {
                const product = filteredProducts.value.find(p => p.id === productId);
                if (!product)
                    continue;
                if (product.timeslots && product.timeslots.length) {
                    const slot = product.timeslots.find(s => s.id === slotId);
                    if (slotId != null && slot) {
                        fetchAvailability(product, slot);
                    }
                }
            }
        }, { immediate: true });
        watch([
            filteredProducts,
            selectedDateFrom,
            selectedDateTo
        ], () => {
            if (!hasActiveFilter.value)
                return;
            for (const product of filteredProducts.value) {
                if (!product.timeslots || !product.timeslots.length) {
                    fetchAvailability(product, null);
                }
                else {
                    // Only fetch availabilities for timeslots valid for the selected day of week
                    for (const slot of getFilteredTimeslots(product)) {
                        fetchAvailability(product, slot);
                    }
                }
            }
        }, { immediate: true });
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_container = _resolveComponent("v-container");
            const _component_v_autocomplete = _resolveComponent("v-autocomplete");
            const _component_v_select = _resolveComponent("v-select");
            const _component_v_form = _resolveComponent("v-form");
            const _component_v_divider = _resolveComponent("v-divider");
            const _component_v_card_title = _resolveComponent("v-card-title");
            const _component_v_progress_circular = _resolveComponent("v-progress-circular");
            const _component_v_card = _resolveComponent("v-card");
            const _component_v_table = _resolveComponent("v-table");
            const _component_v_expand_transition = _resolveComponent("v-expand-transition");
            const _component_v_card_text = _resolveComponent("v-card-text");
            return (dataLoaded.value)
                ? (_openBlock(), _createBlock(_component_v_card, {
                    key: 0,
                    class: "pa-4"
                }, {
                    default: _withCtx(() => [
                        _createVNode(_component_v_container, { class: "text-h4 text-start pa-0 ma-0 mb-4 d-flex align-center" }, {
                            default: _withCtx(() => [
                                _createVNode(_component_v_icon, {
                                    class: "mr-2",
                                    color: "primary"
                                }, {
                                    default: _withCtx(() => _cache[3] || (_cache[3] = [
                                        _createTextVNode("mdi-calendar-check")
                                    ])),
                                    _: 1
                                }),
                                _cache[4] || (_cache[4] = _createTextVNode(" Verfügbarkeiten "))
                            ]),
                            _: 1
                        }),
                        _createVNode(_component_v_form, null, {
                            default: _withCtx(() => [
                                _createVNode(_component_v_container, { class: "d-flex" }, {
                                    default: _withCtx(() => [
                                        _createVNode(KrumosCalendar, {
                                            class: "w-auto mr-4",
                                            "init-date-from": selectedDateFrom.value,
                                            "init-date-to": selectedDateTo.value,
                                            product: selectedProduct.value,
                                            onDateSelected: onDateSelected
                                        }, null, 8, ["init-date-from", "init-date-to", "product"]),
                                        _createVNode(_component_v_container, { class: "pa-0 mb-4" }, {
                                            default: _withCtx(() => [
                                                _createVNode(_component_v_autocomplete, {
                                                    modelValue: selectedProduct.value,
                                                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((selectedProduct).value = $event)),
                                                    items: filteredProducts.value,
                                                    "item-title": "title",
                                                    label: "Produkt auswählen",
                                                    "return-object": "",
                                                    variant: "outlined",
                                                    loading: !products.value.length,
                                                    disabled: !products.value.length,
                                                    "hide-details": "",
                                                    class: "mb-4",
                                                    "data-cy": "select-product",
                                                    clearable: true
                                                }, null, 8, ["modelValue", "items", "loading", "disabled"]),
                                                _createVNode(_component_v_select, {
                                                    modelValue: selectedResourceGroup.value,
                                                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((selectedResourceGroup).value = $event)),
                                                    items: filteredResourceGroups.value,
                                                    "item-title": "name",
                                                    label: "Resourcengruppe auswählen",
                                                    "return-object": "",
                                                    variant: "outlined",
                                                    "hide-details": "",
                                                    class: "mb-4",
                                                    "data-cy": "select-resource-group",
                                                    clearable: true
                                                }, null, 8, ["modelValue", "items"]),
                                                _createVNode(_component_v_select, {
                                                    modelValue: selectedResources.value,
                                                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((selectedResources).value = $event)),
                                                    items: filteredResources.value,
                                                    "item-title": "name",
                                                    label: "Einzelressourcen auswählen",
                                                    "return-object": "",
                                                    multiple: "",
                                                    variant: "outlined",
                                                    "hide-details": "",
                                                    class: "mb-4",
                                                    "data-cy": "select-resources",
                                                    clearable: true
                                                }, null, 8, ["modelValue", "items"])
                                            ]),
                                            _: 1
                                        })
                                    ]),
                                    _: 1
                                })
                            ]),
                            _: 1
                        }),
                        (hasActiveFilter.value)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                _createVNode(_component_v_divider, { class: "mb-2" }),
                                _createVNode(_component_v_container, { class: "text-h5 text-start pa-0 ma-0 mb-4" }, {
                                    default: _withCtx(() => [
                                        _createTextVNode(" Verfügbarkeiten " + _toDisplayString(_unref(DateUtil).isSameDay(selectedDateFrom.value, selectedDateTo.value) ? 'am ' + selectedDateFrom.value.toLocaleDateString() : 'vom ' + selectedDateFrom.value.toLocaleDateString() + ' bis ' + selectedDateTo.value.toLocaleDateString()), 1)
                                    ]),
                                    _: 1
                                }),
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredProducts.value, (product) => {
                                    return (_openBlock(), _createBlock(_component_v_card, {
                                        class: "mb-2",
                                        key: product.id,
                                        "data-cy": "product-availability-card"
                                    }, {
                                        default: _withCtx(() => [
                                            _createVNode(_component_v_card_title, { class: "text-h6 d-flex align-center" }, {
                                                default: _withCtx(() => [
                                                    _createTextVNode(_toDisplayString(product.title), 1)
                                                ]),
                                                _: 2
                                            }, 1024),
                                            _createVNode(_component_v_card_text, null, {
                                                default: _withCtx(() => [
                                                    (product.timeslots && product.timeslots.length)
                                                        ? (_openBlock(), _createBlock(_component_v_container, {
                                                            key: 0,
                                                            class: "d-flex flex-wrap pa-0"
                                                        }, {
                                                            default: _withCtx(() => [
                                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getFilteredTimeslots(product), (slot) => {
                                                                    return (_openBlock(), _createBlock(_component_v_card, {
                                                                        key: slot.id,
                                                                        class: _normalizeClass(["ma-2 pa-2 d-flex flex-column align-center", timeslotCardColor(product, slot)]),
                                                                        style: { "min-width": "200px", "max-width": "220px", "cursor": "pointer" },
                                                                        outlined: "",
                                                                        elevation: selectedTimeslotPerProduct[product.id] === slot.id ? 8 : 2,
                                                                        onClick: ($event) => (selectTimeslot(product.id, slot.id))
                                                                    }, {
                                                                        default: _withCtx(() => [
                                                                            _createElementVNode("div", _hoisted_1, [
                                                                                _createVNode(_component_v_icon, {
                                                                                    class: "mr-1",
                                                                                    color: "info"
                                                                                }, {
                                                                                    default: _withCtx(() => _cache[5] || (_cache[5] = [
                                                                                        _createTextVNode("mdi-clock-outline")
                                                                                    ])),
                                                                                    _: 1
                                                                                }),
                                                                                _createTextVNode(" " + _toDisplayString(_unref(Formatter).timeslot(slot)), 1)
                                                                            ]),
                                                                            _createElementVNode("div", _hoisted_2, [
                                                                                (isLoadingAvailability(product, slot))
                                                                                    ? (_openBlock(), _createBlock(_component_v_progress_circular, {
                                                                                        key: 0,
                                                                                        indeterminate: "",
                                                                                        size: "18",
                                                                                        width: "2",
                                                                                        color: "primary"
                                                                                    }))
                                                                                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                                                        _createVNode(_component_v_icon, {
                                                                                            class: "mr-1",
                                                                                            color: availabilityIconColor(timeslotAvailabilityRatio(product, slot))
                                                                                        }, {
                                                                                            default: _withCtx(() => [
                                                                                                _createTextVNode(_toDisplayString(availabilityIcon(timeslotAvailabilityRatio(product, slot))), 1)
                                                                                            ]),
                                                                                            _: 2
                                                                                        }, 1032, ["color"]),
                                                                                        _createTextVNode(" " + _toDisplayString(_unref(getAssignedResources)(product, _unref(ResourceType).RESOURCE).reduce((sum, r) => sum + getAvailability(product, slot, Number(r.id)), 0)) + " / " + _toDisplayString(_unref(getAssignedResources)(product, _unref(ResourceType).RESOURCE).reduce((sum, r) => sum + (Number(r.totalQuantity) ?? 0), 0)), 1)
                                                                                    ], 64))
                                                                            ])
                                                                        ]),
                                                                        _: 2
                                                                    }, 1032, ["class", "elevation", "onClick"]));
                                                                }), 128))
                                                            ]),
                                                            _: 2
                                                        }, 1024))
                                                        : _createCommentVNode("", true),
                                                    _createVNode(_component_v_expand_transition, null, {
                                                        default: _withCtx(() => [
                                                            (shouldShowResourceTable(product))
                                                                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                                                                    _createVNode(_component_v_table, { density: "compact" }, {
                                                                        default: _withCtx(() => [
                                                                            _cache[6] || (_cache[6] = _createElementVNode("thead", null, [
                                                                                _createElementVNode("tr", null, [
                                                                                    _createElementVNode("th"),
                                                                                    _createElementVNode("th", null, "Ressource"),
                                                                                    _createElementVNode("th", null, "Verfügbar"),
                                                                                    _createElementVNode("th", null, "Gesamt")
                                                                                ])
                                                                            ], -1)),
                                                                            _createElementVNode("tbody", null, [
                                                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(getAssignedResources)(product, _unref(ResourceType).RESOURCE), (resource) => {
                                                                                    return (_openBlock(), _createElementBlock("tr", {
                                                                                        key: Number(resource.id)
                                                                                    }, [
                                                                                        _createElementVNode("td", null, [
                                                                                            _createVNode(_component_v_icon, {
                                                                                                color: availabilityIconColor(resourceAvailabilityRatio(product, resource))
                                                                                            }, {
                                                                                                default: _withCtx(() => [
                                                                                                    _createTextVNode(_toDisplayString(availabilityIcon(resourceAvailabilityRatio(product, resource))), 1)
                                                                                                ]),
                                                                                                _: 2
                                                                                            }, 1032, ["color"])
                                                                                        ]),
                                                                                        _createElementVNode("td", null, _toDisplayString(resource.name), 1),
                                                                                        _createElementVNode("td", null, [
                                                                                            (isLoadingAvailability(product, getSelectedSlot(product)))
                                                                                                ? (_openBlock(), _createBlock(_component_v_progress_circular, {
                                                                                                    key: 0,
                                                                                                    indeterminate: "",
                                                                                                    size: "18",
                                                                                                    width: "2",
                                                                                                    color: "primary"
                                                                                                }))
                                                                                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                                                                    _createTextVNode(_toDisplayString(getAvailability(product, getSelectedSlot(product), Number(resource.id))), 1)
                                                                                                ], 64))
                                                                                        ]),
                                                                                        _createElementVNode("td", null, _toDisplayString(resource.totalQuantity), 1)
                                                                                    ]));
                                                                                }), 128))
                                                                            ])
                                                                        ]),
                                                                        _: 2
                                                                    }, 1024)
                                                                ]))
                                                                : _createCommentVNode("", true)
                                                        ]),
                                                        _: 2
                                                    }, 1024)
                                                ]),
                                                _: 2
                                            }, 1024)
                                        ]),
                                        _: 2
                                    }, 1024));
                                }), 128))
                            ], 64))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                }))
                : _createCommentVNode("", true);
        };
    }
});
